import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
} from "@reduxjs/toolkit";
import { RootState } from "../../app/store";

import { isPast } from "date-fns";
import { EventPayload } from "../../common/types/Event";
import { EditEntry, Entry, EntryFilters } from "../entries/entriesAPI";
import {
  AddTalentWorkHistoryEntry,
  RemoveTalentWorkHistoryEntry,
  UpdateTalentWorkHistoryEntry,
} from "./types/commands";
import {
  TalentWorkHistoryEntryAdded,
  TalentWorkHistoryEntryRemoved,
  TalentWorkHistoryEntryUpdated,
} from "./types/events";
import {
  WorkHistory,
  WorkHistoryById,
  WorkHistorySummaryById,
  WorkHistorySummaryResponse,
  WorkLocationSummary,
} from "./types/queries";
import {
  listWorkHistoriesByTalentId,
  listWorkHistorySummaryByTalentId,
  postAddTalentWorkHistoryEntry,
  postRemoveTalentWorkHistoryEntry,
  postUpdateTalentWorkHistoryEntry,
} from "./workHistoriesAPI";

const workHistoriesAdapter = createEntityAdapter<WorkHistory>(); // TODO: fix when WorkHistory type is fixed
const absenceEntriesAdapter = createEntityAdapter<Entry>();

const withUiPermissions = (workHistory: WorkHistory): WorkHistory => {
  const nonEezyWorkMigratedOrExpertAdded =
    !workHistory.isEezyWork ||
    workHistory.source === "Migration" ||
    workHistory.source === "Expert";
  return {
    ...workHistory,
    editable: nonEezyWorkMigratedOrExpertAdded,
    deletable: nonEezyWorkMigratedOrExpertAdded,
    dayCountEditable:
      workHistory.source === "Migration" || workHistory.source === "Expert",
  };
};

export type SliceState = EntityState<WorkHistory, WorkHistory["id"]> & {
  cursor: string | null;
  filters: EntryFilters;
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
  entry: EditEntry | null;
  absenceEntries: EntityState<Entry, Entry["id"]>;
  summary: {
    data: WorkLocationSummary[];
    status: "idle" | "pending" | "succeeded" | "failed";
  };
};

export const fetchWorkHistoriesByTalentId = createAsyncThunk<
  WorkHistory[],
  WorkHistoryById,
  { state: RootState }
>("workHistories/fetchWorkHistories", async (params, { requestId }) => {
  const res = await listWorkHistoriesByTalentId(params, { requestId });
  return res.data;
});

export const fetchWorkHistorySummaryByTalentId = createAsyncThunk<
  WorkHistorySummaryResponse,
  WorkHistorySummaryById,
  { state: RootState }
>("workHistories/fetchWorkHistorySummary", async (params, { requestId }) => {
  const res = await listWorkHistorySummaryByTalentId(params, { requestId });
  return res.data;
});

export const addTalentWorkHistoryEntry = createAsyncThunk(
  "talents/addTalentWorkHistoryEntry",
  async (
    command: AddTalentWorkHistoryEntry,
    { requestId },
  ): Promise<EventPayload<TalentWorkHistoryEntryAdded>> =>
    await postAddTalentWorkHistoryEntry(command, { requestId }),
);

export const updateTalentWorkHistoryEntry = createAsyncThunk(
  "talents/updateTalentWorkHistoryEntry",
  async (
    command: UpdateTalentWorkHistoryEntry,
    { requestId },
  ): Promise<EventPayload<TalentWorkHistoryEntryUpdated>> =>
    await postUpdateTalentWorkHistoryEntry(command, { requestId }),
);

export const removeTalentWorkHistoryEntry = createAsyncThunk(
  "talents/removeTalentWorkHistoryEntry",
  async (
    command: RemoveTalentWorkHistoryEntry,
    { requestId },
  ): Promise<EventPayload<TalentWorkHistoryEntryRemoved>> =>
    await postRemoveTalentWorkHistoryEntry(command, { requestId }),
);

export const workHistoriesSlice = createSlice({
  name: "workHistories",
  initialState: workHistoriesAdapter.getInitialState({
    cursor: null,
    filters: {
      sort: "",
      pageSize: "",
    },
    status: "idle",
    entry: null,
    absenceEntries: absenceEntriesAdapter.getInitialState(),
    summary: {
      data: [],
      status: "idle",
    },
  }) as SliceState,
  reducers: {
    clearWorkHistories: (state) => {
      workHistoriesAdapter.removeAll(state);
      state.status = "idle";
      state.summary = { data: [], status: "idle" };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchWorkHistoriesByTalentId.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchWorkHistoriesByTalentId.fulfilled, (state, action) => {
      workHistoriesAdapter.setAll(
        state,
        action.payload.map((entry) => withUiPermissions(entry)),
      );
      state.status = "succeeded";
    });
    builder.addCase(fetchWorkHistoriesByTalentId.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchWorkHistorySummaryByTalentId.pending, (state) => {
      state.summary.status = "pending";
    });
    builder.addCase(
      fetchWorkHistorySummaryByTalentId.fulfilled,
      (state, action) => {
        state.summary.data = action.payload.workLocations;
        state.summary.status = "succeeded";
      },
    );
    builder.addCase(fetchWorkHistorySummaryByTalentId.rejected, (state) => {
      state.summary.status = "failed";
    });
    builder.addCase(addTalentWorkHistoryEntry.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(addTalentWorkHistoryEntry.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(addTalentWorkHistoryEntry.fulfilled, (state, action) => {
      const event = action.payload;
      const addedWorkHistoryEntry = action.meta.arg.workHistory;

      const addedEntity: WorkHistory = {
        id: event.id,
        version: event.version,
        source: "Expert",
        workLocationName: addedWorkHistoryEntry.workLocationName || "",
        workLocationId: addedWorkHistoryEntry.workLocationId || undefined,
        customerOrgId: addedWorkHistoryEntry.customerOrgId || undefined,
        customerOrgName: addedWorkHistoryEntry.customerOrgName || undefined,
        employerId: addedWorkHistoryEntry.employerId || undefined,
        employerName: addedWorkHistoryEntry.employerName || undefined,
        employerBusinessId:
          addedWorkHistoryEntry.employerBusinessId || undefined,
        workTitleId: addedWorkHistoryEntry.workTitleId,
        workTitleName: addedWorkHistoryEntry.workTitleName,
        jobDescription: addedWorkHistoryEntry.jobDescription,
        city: addedWorkHistoryEntry.city,
        startDate: addedWorkHistoryEntry.startDate,
        endDate: addedWorkHistoryEntry.endDate,
        isEezyWork: addedWorkHistoryEntry.workLocationId ? true : false,
        dayCount: action.meta.arg.dayCount,
      };

      workHistoriesAdapter.addOne(state, withUiPermissions(addedEntity));

      state.status = "succeeded";
    });
    builder.addCase(updateTalentWorkHistoryEntry.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateTalentWorkHistoryEntry.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateTalentWorkHistoryEntry.fulfilled, (state, action) => {
      const event = action.payload;
      const updatedWorkHistory = action.meta.arg.workHistory;
      const existingEntity = state.entities[event.id] as
        | WorkHistory
        | undefined;

      const updatedEntity: WorkHistory = {
        id: event.id,
        version: event.version,
        source: existingEntity?.source,
        workLocationId: updatedWorkHistory.workLocationId || undefined,
        workLocationName: updatedWorkHistory.workLocationName || "",
        customerOrgId: existingEntity?.customerOrgId,
        customerOrgName: existingEntity?.customerOrgName,
        employerId: existingEntity?.employerId,
        employerName: existingEntity?.employerName,
        employerBusinessId:
          updatedWorkHistory.employerBusinessId ??
          existingEntity?.employerBusinessId,
        workTitleId: updatedWorkHistory.workTitleId,
        workTitleName: updatedWorkHistory.workTitleName,
        jobDescription: updatedWorkHistory.jobDescription,
        city: updatedWorkHistory.city,
        endDate: updatedWorkHistory.endDate,
        startDate: updatedWorkHistory.startDate,
        // Preserve Eezy flag for migrated/legacy entries where workLocationId can be missing.
        isEezyWork:
          existingEntity?.isEezyWork ?? (updatedWorkHistory.workLocationId ? true : false),
        dayCount:
          action.meta.arg.dayCount ?? existingEntity?.dayCount ?? undefined,
      };

      workHistoriesAdapter.setOne(state, withUiPermissions(updatedEntity));
      state.status = "succeeded";
    });
    builder.addCase(removeTalentWorkHistoryEntry.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(removeTalentWorkHistoryEntry.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(removeTalentWorkHistoryEntry.fulfilled, (state, action) => {
      const { id } = action.payload;
      workHistoriesAdapter.removeOne(state, id);
      state.status = "succeeded";
    });
  },
});

export const selectAllEezyWorkHistories = createSelector(
  (state: RootState) => state.workHistories.entities,
  (entities: Record<string, WorkHistory>) =>
    Object.values(entities).filter(
      (workHistory: WorkHistory) => workHistory.isEezyWork,
    ),
);

export const selectAllPastEezyWorkHistories = createSelector(
  (state: RootState) => state.workHistories.entities,
  (entities: Record<string, WorkHistory>) =>
    Object.values(entities).filter(
      (workHistory: WorkHistory) =>
        workHistory?.endDate &&
        isPast(new Date(workHistory.startDate)) &&
        isPast(new Date(workHistory.endDate)) &&
        workHistory.isEezyWork,
    ),
);

export const selectWorkHistorySummary = (state: RootState) =>
  state.workHistories.summary.data;

export const {
  selectAll: selectAllWorkhistories,
  selectById: selectWorkHistoryById,
} = workHistoriesAdapter.getSelectors(
  (state: RootState) => state.workHistories,
);

export const { clearWorkHistories } = workHistoriesSlice.actions;

export const workhistoriesReducer = workHistoriesSlice.reducer;
